import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Textarea, Select } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { useDraftPersistence, clearDraft } from "@/lib/useDraftPersistence";
import { GLASS_AREAS, GLASS_DAMAGE } from "@/lib/vda";

export default function DriverGlass() {
  const nav = useNavigate();
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [form, setForm] = useState({
    area: "windscreen", damage: "chip", adas: "unknown", obscures_view: "no",
    description: "", address: {},
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draftScope = ctx?.vehicle?.id ? `glass_${ctx.vehicle.id}` : null;
  useDraftPersistence({ scope: draftScope, form, setForm, enabled: !!draftScope, onRestore: () => toast.info("Restored your previous progress") });

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const details = {
        flow: "windscreen_glass",
        area: form.area, area_label: GLASS_AREAS.find((a) => a.value === form.area)?.label,
        damage: form.damage, damage_label: GLASS_DAMAGE.find((d) => d.value === form.damage)?.label,
        adas: form.adas, obscures_view: form.obscures_view === "yes",
      };
      if (form.address?.address_text) details.location = form.address;
      const id = await createBooking(ctx, {
        request_type: "windscreen", request_category: "windscreen_glass", request_details: details,
        safe_to_drive: form.obscures_view !== "yes",
        description: form.description || null,
        location_text: form.address?.address_text || null, postcode: form.address?.postcode || null,
        priority: form.obscures_view === "yes" ? "high" : "normal",
      });
      clearDraft(draftScope);
      toast.success("Glass request submitted and routed to your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "We couldn't submit your request. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title="Windscreen & glass">
      <form onSubmit={submit} className="space-y-4" data-testid="glass-form">
        <Field label="Which glass?">
          <Select data-testid="glass-area" value={form.area} onChange={(e) => set("area", e.target.value)}>
            {GLASS_AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </Select>
        </Field>
        <Field label="Type of damage">
          <Select data-testid="glass-damage" value={form.damage} onChange={(e) => set("damage", e.target.value)}>
            {GLASS_DAMAGE.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>
        </Field>
        <Field label="Is the damage in the driver's line of sight?">
          <div className="grid grid-cols-2 gap-3">
            {["no", "yes"].map((v) => (
              <button key={v} type="button" data-testid={`glass-obscures-${v}`} onClick={() => set("obscures_view", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.obscures_view === v ? (v === "yes" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>
                {v === "yes" ? "Yes, in view" : "No"}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Does the vehicle have a front-facing camera / driver assist? (ADAS)">
          <Select data-testid="glass-adas" value={form.adas} onChange={(e) => set("adas", e.target.value)}>
            <option value="unknown">Not sure</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </Field>
        {form.obscures_view === "yes" && (
          <Panel className="p-3 text-sm bg-[hsl(var(--vda-red))]/8 border-[hsl(var(--vda-red))]/30">
            Damage in your line of sight can make the vehicle unsafe or illegal to drive. Use the <strong>Emergency</strong> tab if you need to speak to someone now.
          </Panel>
        )}
        <AddressLookup label="Where is the vehicle?" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="glass-address" />
        <Field label="Anything else?">
          <Textarea data-testid="glass-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="How and when it happened" />
        </Field>
        <Btn type="submit" loading={saving} data-testid="glass-submit" className="w-full">Submit request</Btn>
      </form>
    </DriverShell>
  );
}
